import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFilesList } from './customHooks/useFilesList';
import ReactLoading from 'react-loading';
import {
  Close,
  List,
  ListHeader,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ScrollMenu,
  ModalOverlay,
} from './style';
import { FiEye } from 'react-icons/fi';
import { IoMdTrash } from 'react-icons/io';
import { ConfirmDeleteFileModal } from './utils/ConfirmDeleteFile';
import FileButton from './utils/FileButton';
import moment from 'moment';
import AdditionalFileInformationModal from '../AdditionalFileInformationModal';

const FilesModalExcel = ({ isOpen, onClose, moduleUuid, canDeleteFiles }) => {
  const [isOpenConfirmDeletionModal, setIsOpenConfirmDeletionModal] = useState(
    false
  );
  const [
    isOpenAditionalFileInformationModal,
    setIsOpenAdditionalFileInformationModal,
  ] = useState(false);
  const [file, setFile] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const {
    fetchFilesById,
    files,
    loading,
    loadList,
    attachNewFile,
    downloadFileFromAWS,
    deleteFile,
    setLoadList,
  } = useFilesList(moduleUuid);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchFilesById(moduleUuid);
    }
  }, [isOpen]);

  const handleFileUpload = event => {
    const file = event.target.files[0];
    setFile(file);
    setIsOpenAdditionalFileInformationModal(true);
  };

  const handleAdditionalFileInformationModalClose = () => {
    setIsOpenAdditionalFileInformationModal(false);
  };

  const handleSubmitAdditionalFileInformation = data => {
    attachNewFile(file, data);
    setFile(null);
    handleAdditionalFileInformationModalClose();
    fileInputRef.current.value = '';
  };

  const handleOutsideClick = event => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target) &&
      !isOpenConfirmDeletionModal
    ) {
      onClose();
    }
  };

  const handlePreviewFile = async fileItem => {
    try {
      setLoadingPreview(fileItem.uuid);
      await downloadFileFromAWS(fileItem?.file?.url);
    } finally {
      setLoadingPreview(null);
    }
  };

  return isOpen ? (
    <ModalOverlay onClick={handleOutsideClick}>
      <Modal isOpen={isOpen}>
        <ModalContent ref={modalRef}>
          <ModalHeader>
            <Close onClick={onClose}>&times;</Close>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileButton
                fileInputRef={fileInputRef}
                attachNewFile={handleFileUpload}
              />
            </div>
          </ModalHeader>
          <ModalBody>
            <ScrollMenu>
              <ListHeader>
                <p>Nome do Arquivo</p>
                <p>Data de Vencimento</p>
              </ListHeader>
              {loading ? (
                <div
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    display: 'flex',
                    margin: '32px 0',
                  }}
                >
                  <ReactLoading
                    type="spin"
                    color="#011826"
                    height={24}
                    width={24}
                  />
                </div>
              ) : !loading && files?.length > 0 ? (
                <List>
                  {files.map(item => (
                    <ListItem key={item.uuid}>
                      <p className="file-name" title={item.file.name}>
                        {item.file.name}
                      </p>
                      <p>
                        {item.dueDate ? moment(item.dueDate).format(
                          'DD/MM/YYYY HH:mm'
                        ) : '--/--/---- --:--'}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '1rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor:
                              loadingPreview === item.uuid ? 'wait' : 'pointer',
                          }}
                          onClick={() => handlePreviewFile(item)}
                        >
                          {loadingPreview === item.uuid ? (
                            <ReactLoading
                              type="spin"
                              color="#011826"
                              height={20}
                              width={20}
                            />
                          ) : (
                            <FiEye
                              style={{ width: '1.2rem', height: '1.2rem' }}
                            />
                          )}
                          <span style={{ fontSize: '0.8rem' }}>
                            {loadingPreview === item.uuid
                              ? 'Abrindo...'
                              : 'Visualizar'}
                          </span>
                        </div>
                        {canDeleteFiles && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setIsOpenConfirmDeletionModal(true);
                              setFileToDelete(item.file);
                            }}
                          >
                            <IoMdTrash
                              style={{ width: '1.2rem', height: '1.2rem' }}
                            />
                            <span style={{ fontSize: '0.8rem' }}>Deletar</span>
                          </div>
                        )}
                      </div>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <p style={{ textAlign: 'center' }}>
                  Nenhum arquivo disponível.
                </p>
              )}
            </ScrollMenu>
          </ModalBody>
        </ModalContent>
        <ConfirmDeleteFileModal
          moduleUuid={moduleUuid}
          isOpen={isOpenConfirmDeletionModal}
          setIsOpenConfirmDeletionModal={setIsOpenConfirmDeletionModal}
          file={fileToDelete}
          setLoadList={setLoadList}
          loadList={loadList}
          deleteFile={deleteFile}
        />

        <AdditionalFileInformationModal
          isOpen={isOpenAditionalFileInformationModal}
          onClose={handleAdditionalFileInformationModalClose}
          onSubmitData={handleSubmitAdditionalFileInformation}
          fileName={file ? file.name : null}
        />
      </Modal>
    </ModalOverlay>
  ) : null;
};

FilesModalExcel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  moduleUuid: PropTypes.string.isRequired,
};

export default FilesModalExcel;
